import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { CheckCircle2, RotateCcw, Save, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { ledgerApi, apiError } from '../../../lib/api';
import { AccountSelect, acct, DateField, Empty, money, PageTitle, SOURCE_LABEL, today, useAccounts } from './shared';

const bankLike = (a) => a.type === 'Bank' || a.type === 'Credit Card';

export default function Reconcile() {
  const [accounts] = useAccounts();
  const [accountId, setAccountId] = useState('');
  const [through, setThrough] = useState(today());
  const [ending, setEnding] = useState('');
  const [data, setData] = useState(null);
  const [cleared, setCleared] = useState(new Set());
  const [busy, setBusy] = useState(false);
  // A saved draft's statement date is adopted once per account, so picking a new date afterwards sticks.
  const adoptedFor = useRef('');

  useEffect(() => {
    if (!accountId) { const first = accounts.find(bankLike); if (first) setAccountId(first.id); }
  }, [accounts, accountId]);

  const load = useCallback((keepDraft = true) => {
    if (!accountId) return;
    ledgerApi.reconcile(accountId, through).then((d) => {
      setData(d);
      if (keepDraft && d.draft && adoptedFor.current !== accountId) {
        adoptedFor.current = accountId;
        setCleared(new Set(d.draft.cleared || []));
        setEnding(String(d.draft.endingBalance ?? ''));
        if (d.draft.statementDate && d.draft.statementDate !== through) setThrough(d.draft.statementDate);
      } else if (!keepDraft) {
        setCleared(new Set());
      }
    }).catch((e) => toast.error(apiError(e, 'Could not load the account')));
  }, [accountId, through]);
  useEffect(() => { load(true); }, [load]);

  const lines = useMemo(() => data?.lines || [], [data]);
  const deposits = lines.filter((l) => l.amount > 0);
  const withdrawals = lines.filter((l) => l.amount < 0);
  const clearedTotal = lines.filter((l) => cleared.has(l.key)).reduce((a, l) => a + l.amount, 0);
  const beginning = data?.beginningBalance || 0;
  const endingNum = Number(ending);
  const difference = Math.round(((ending === '' ? 0 : endingNum) - (beginning + clearedTotal)) * 100) / 100;

  const toggle = (key) => setCleared((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const tickMatched = () => setCleared((s) => new Set([...s, ...lines.filter((l) => l.bankMatched).map((l) => l.key)]));

  const save = async (finish) => {
    if (ending === '' || Number.isNaN(endingNum)) { toast.error('Enter the ending balance from the bank statement'); return; }
    setBusy(true);
    try {
      await ledgerApi.saveReconcile({ accountId, statementDate: through, endingBalance: endingNum, cleared: [...cleared], finish });
      toast.success(finish ? 'Reconciliation finished' : 'Progress saved');
      if (finish) { setEnding(''); setCleared(new Set()); }
      load(!finish);
    } catch (e) {
      toast.error(apiError(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  const undo = async (r) => {
    if (!window.confirm(`Reopen the reconciliation through ${r.statementDate}? Its ticked items go back to unreconciled.`)) return;
    try { await ledgerApi.undoReconcile(r.id); toast.success('Reconciliation reopened'); load(true); } catch (e) { toast.error(apiError(e)); }
  };

  const table = (title, list) => (
    <Card className="border-slate-200">
      <div className="px-4 py-3 border-b border-slate-200 font-bold text-slate-900 flex justify-between">
        <span>{title}</span><span className="text-sm text-slate-500 font-normal">{list.filter((l) => cleared.has(l.key)).length} of {list.length} ticked</span>
      </div>
      {!list.length ? <Empty>None.</Empty> : (
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            {list.map((l) => (
              <tr key={l.key} className={`cursor-pointer ${cleared.has(l.key) ? 'bg-emerald-50/60' : 'hover:bg-slate-50'}`} onClick={() => toggle(l.key)}>
                <td className="pl-4 py-2 w-8"><Checkbox checked={cleared.has(l.key)} onCheckedChange={() => toggle(l.key)} onClick={(e) => e.stopPropagation()} /></td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{l.date}</td>
                <td className="px-3 py-2 text-slate-800">{l.memo}{l.bankMatched ? <Badge className="ml-2 border-0 bg-blue-50 text-blue-700">In bank feed</Badge> : null}</td>
                <td className="px-3 py-2 text-slate-500 text-xs">{SOURCE_LABEL[l.source] || l.source}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold">{money(Math.abs(l.amount))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-reconcile">
      <PageTitle title="Reconcile" subtitle="Tick what appears on the bank statement until the difference is zero.">
        <div>
          <Label className="text-xs text-slate-500">Account</Label>
          <AccountSelect accounts={accounts} filter={bankLike} value={accountId} onChange={(v) => { setAccountId(v); setEnding(''); setCleared(new Set()); }} className="mt-1 h-9 w-64" testId="rec-account" />
        </div>
        <DateField label="Statement date" value={through} onChange={setThrough} testId="rec-date" />
        <div>
          <Label className="text-xs text-slate-500">Statement ending balance</Label>
          <Input type="number" step="0.01" value={ending} onChange={(e) => setEnding(e.target.value)} className="mt-1 h-9 w-40 text-right" placeholder="0.00" data-testid="rec-ending" />
        </div>
      </PageTitle>

      {!accounts.some(bankLike) ? <Card className="p-6 text-sm text-slate-600">Add a Bank or Credit Card account in the Chart of Accounts first.</Card> : null}

      {data ? (
        <>
          <div className="grid sm:grid-cols-4 gap-3">
            {[['Beginning balance', beginning], ['Ticked this time', clearedTotal], ['Statement ending', ending === '' ? null : endingNum]].map(([k, v]) => (
              <Card key={k} className="p-4 border-slate-200"><div className="text-xs text-slate-500">{k}</div><div className="text-xl font-extrabold tabular-nums mt-1">{v === null ? '—' : acct(v)}</div></Card>
            ))}
            <Card className={`p-4 ${difference === 0 && ending !== '' ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
              <div className="text-xs text-slate-600">Difference</div>
              <div className="text-xl font-extrabold tabular-nums mt-1" data-testid="rec-difference">{money(difference)}</div>
            </Card>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" className="gap-1.5" onClick={tickMatched} disabled={!lines.some((l) => l.bankMatched)}><Wand2 className="w-4 h-4" />Tick everything matched in the bank feed</Button>
            <Button variant="outline" className="gap-1.5" onClick={() => save(false)} disabled={busy}><Save className="w-4 h-4" />Save for later</Button>
            <Button className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" onClick={() => save(true)} disabled={busy || difference !== 0 || ending === ''} data-testid="rec-finish"><CheckCircle2 className="w-4 h-4" />Finish</Button>
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            {table('Deposits & credits', deposits)}
            {table('Payments & withdrawals', withdrawals)}
          </div>
          {data.history?.length ? (
            <Card className="border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200 font-bold text-slate-900">History</div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {data.history.map((r, i) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2 text-slate-700">Through {r.statementDate}</td>
                      <td className="px-4 py-2"><Badge className={`border-0 ${r.status === 'Closed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>{r.status === 'Closed' ? 'Reconciled' : 'In progress'}</Badge></td>
                      <td className="px-4 py-2 text-right tabular-nums">{money(r.endingBalance)}</td>
                      <td className="px-4 py-2 text-slate-500 text-xs">{r.closedBy || r.updatedBy}</td>
                      <td className="px-4 py-2 text-right">{r.status === 'Closed' && i === data.history.findIndex((h) => h.status === 'Closed') ? (
                        <Button size="sm" variant="ghost" className="h-7 gap-1 text-slate-500" onClick={() => undo(r)}><RotateCcw className="w-3.5 h-3.5" />Reopen</Button>
                      ) : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
